// /scada/control — «Керування парком»: усі станції на одному екрані.
//
// Запит диспетчерів (зустріч 16.10): «дуже зручно, коли на одному екрані для
// кожної станції видно значення». Рядок — станція: фактична потужність,
// завантаження, активне обмеження, інвертори, УЗЕ, звʼязок.
//
// ЗАПИСУ УСТАВОК ТУТ НЕМАЄ. Попередня версія сторінки імітувала виконання
// команд: «Виконано ✓» або «Немає підтвердження» з'являлися за таймером,
// без жодного запису в обладнання. Для диспетчерського екрана це небезпечно —
// людина могла б вважати, що обмеження задане. Запис з'явиться разом із
// каналом до EMS box станції (SunSpec, ТЗ EMS box § 10), з підтвердженням за
// зворотним читанням і фактичною реакцією обладнання, а не за таймером.
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { SectionCard, StatusBadge } from '@/components/scada/kit';
import { scada } from '@/scada/tokens';
import { STATIONS } from '@/scada/mock/stations';
import { useFleetTelemetry } from '@/scada/useTelemetry';
import { useAllowedStationIds } from '@/scada/useScopedStations';
import { useScadaStation } from '@/scada/useScadaStation';
import { stationPath } from '@/scada/navConfig';
import { cadenceOf, linkOf, fmtAge, LINK_STYLE, kw0, mw2, n1, signed0 } from '@/scada/live';

const cfgOf = (id: string) => STATIONS.find((s) => s.id === id);

const LIMIT_LABEL: Record<string, string> = {
  get sko() { return i18n.t('ak_scada_sko'); }, get operational_safety() { return i18n.t('ak_scada_operational_safety'); }, get technical() { return i18n.t('ak_scada_dso_technical_limit'); }, get manual() { return i18n.t('ak_scada_manual'); },
};

const th = { textAlign: 'left', color: scada.textMuted, fontSize: 11, fontWeight: 600, padding: '8px 12px', whiteSpace: 'nowrap' } as const;
const td = { padding: '10px 12px', color: scada.text, fontSize: 13, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

export default function ScadaControlPage() {
  useTranslation();
  const { data: rawData } = useFleetTelemetry();
  const allowed = useAllowedStationIds();
  const station = useScadaStation();

  // Увесь парк у межах прав доступу — НЕ лише станція з селектора: саме
  // огляд усіх станцій разом і просили диспетчери. Вибрану підсвічуємо.
  const rows = useMemo(() => {
    const scoped = allowed ? rawData.filter((d) => allowed.has(d.stationId)) : rawData;
    return [...scoped].sort((a, b) => (cfgOf(a.stationId)?.name ?? '').localeCompare(cfgOf(b.stationId)?.name ?? '', 'uk'));
  }, [rawData, allowed]);

  const totals = rows.reduce(
    (acc, d) => {
      const cap = cfgOf(d.stationId)?.acCapacityKw ?? 0;
      const link = linkOf(d.comms?.lastPacketSec, cadenceOf(d.stationId));
      const fresh = link === 'online' || link === 'stale';
      return {
        p: acc.p + (fresh ? d.activePowerKw : 0),
        unknown: acc.unknown + (fresh ? 0 : 1),
        cap: acc.cap + cap,
        inv: acc.inv + d.invertersWorking,
        invTotal: acc.invTotal + d.inverterCount,
        bess: acc.bess + (fresh && d.bess ? d.bess.powerKw : 0),
      };
    },
    { p: 0, unknown: 0, cap: 0, inv: 0, invTotal: 0, bess: 0 },
  );

  return (
    <>
      <div style={{
        background: `${scada.state.curtailed}12`, border: `1px solid ${scada.state.curtailed}40`, color: scada.text,
        borderRadius: 10, padding: '10px 14px', fontSize: 12, marginBottom: 16, lineHeight: 1.5,
      }}>
        <b style={{ color: scada.state.curtailed }}>{i18n.t('ak_scada_view_only_mode')}</b>{' '}
        {i18n.t('ak_scada_writing_setpoints_from_the_cabinet_is')}
      </div>

      <SectionCard
        title={i18n.t('ak_scada_fleet_control')}
        subtitle={i18n.t('ak_scada_stations_power_limits_and_link_status', { n: rows.length })}
        right={
          <span style={{ color: scada.textMuted, fontSize: 11, whiteSpace: 'nowrap' }}>
            Σ <b style={{ color: scada.text }}>{mw2(totals.p)}</b> {i18n.t('ak_scada_mw_of')} {mw2(totals.cap)}
            {totals.unknown > 0 && <span style={{ color: scada.state.warning }}>{i18n.t('ak_scada_no_data_2', { n: totals.unknown })}</span>}
          </span>
        }
        padded={false}
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
            <thead>
              <tr style={{ background: scada.panel2 }}>
                {[i18n.t('ak_scada_station'), i18n.t('ak_scada_status_2'), i18n.t('ak_scada_link'), i18n.t('ak_scada_p_actual_mw'), i18n.t('ak_scada_installed_mw'), i18n.t('ak_scada_load_2'), i18n.t('ak_scada_limit'), i18n.t('ak_scada_inverters'), i18n.t('ak_scada_bess'), i18n.t('ak_scada_control_channel')].map((h) => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => {
                const cfg = cfgOf(d.stationId);
                const cap = cfg?.acCapacityKw ?? 0;
                const age = d.comms?.lastPacketSec ?? null;
                const link = linkOf(age, cadenceOf(d.stationId));
                const fresh = link === 'online' || link === 'stale';
                const load = fresh && cap ? (d.activePowerKw / cap) * 100 : null;
                const selected = station.id === d.stationId;
                return (
                  <tr key={d.stationId} style={{ borderTop: `1px solid ${scada.border}`, background: selected ? scada.panel2 : undefined }}>
                    <td style={{ ...td, fontWeight: 600 }}>
                      <Link to={stationPath(d.stationId)} style={{ color: scada.text, textDecoration: 'none' }}>
                        {cfg?.name ?? d.stationId}
                      </Link>
                    </td>
                    <td style={td}><StatusBadge state={d.state} small /></td>
                    <td style={td}>
                      <span className="inline-flex items-center" style={{ gap: 6 }}>
                        <span style={{ width: 7, height: 7, borderRadius: 999, background: LINK_STYLE[link].c }} />
                        <span style={{ color: scada.textDim, fontSize: 12 }}>{age == null ? LINK_STYLE[link].label : fmtAge(age)}</span>
                      </span>
                    </td>
                    <td style={{ ...td, fontWeight: 600 }}>{fresh ? mw2(d.activePowerKw) : '—'}</td>
                    <td style={{ ...td, color: scada.textDim }}>{mw2(cap)}</td>
                    <td style={td}>
                      {load == null ? <span style={{ color: scada.textMuted }}>—</span> : (
                        <span className="inline-flex items-center" style={{ gap: 8 }}>
                          <span style={{ width: 64, height: 4, borderRadius: 2, background: scada.border, overflow: 'hidden', display: 'inline-block' }}>
                            <span style={{ display: 'block', height: '100%', width: `${Math.min(100, Math.max(0, load))}%`, background: scada.state.ok }} />
                          </span>
                          <span style={{ fontSize: 12 }}>{n1(load)} %</span>
                        </span>
                      )}
                    </td>
                    <td style={td}>
                      {d.activeLimitKw != null ? (
                        <span>
                          <b style={{ color: scada.state.curtailed }}>{kw0(d.activeLimitKw)} {i18n.t('ak_scada_kw')}</b>
                          <span style={{ color: scada.textMuted, fontSize: 11 }}>{i18n.t('ak_scada_x', { p: cap ? Math.round((d.activeLimitKw / cap) * 100) : '—', r: d.limitReason ? LIMIT_LABEL[d.limitReason] ?? d.limitReason : i18n.t('ak_scada_source_unknown') })}</span>
                        </span>
                      ) : <span style={{ color: scada.textMuted }}>{i18n.t('ak_scada_none')}</span>}
                    </td>
                    <td style={{ ...td, color: d.invertersWorking < d.inverterCount ? scada.state.warning : scada.text }}>
                      {d.invertersWorking}/{d.inverterCount}
                    </td>
                    <td style={td}>
                      {d.bess && fresh
                        ? <span>{signed0(d.bess.powerKw)} {i18n.t('ak_scada_kw')} <span style={{ color: scada.textMuted, fontSize: 11 }}>· {n1(d.bess.socPct)} %</span></span>
                        : <span style={{ color: scada.textMuted }}>{d.hasBess ? '—' : i18n.t('ak_scada_none')}</span>}
                    </td>
                    <td style={{ ...td, color: scada.textMuted, fontSize: 12 }}>{i18n.t('ak_scada_view_only')}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: `1px solid ${scada.borderStrong}`, background: scada.panel2 }}>
                <td style={{ ...td, fontWeight: 700 }}>{i18n.t('ak_scada_total_2')}</td>
                <td style={td} />
                <td style={td} />
                <td style={{ ...td, fontWeight: 700 }}>{mw2(totals.p)}</td>
                <td style={{ ...td, color: scada.textDim }}>{mw2(totals.cap)}</td>
                <td style={td}>{totals.cap ? `${n1((totals.p / totals.cap) * 100)} %` : '—'}</td>
                <td style={td} />
                <td style={td}>{totals.inv}/{totals.invTotal}</td>
                <td style={td}>{signed0(totals.bess)} {i18n.t('ak_scada_kw')}</td>
                <td style={td} />
              </tr>
            </tfoot>
          </table>
        </div>
      </SectionCard>

      <div style={{ color: scada.textMuted, fontSize: 11, marginTop: 10, lineHeight: 1.5 }}>
        {i18n.t('ak_scada_link_online_means_data_no_older_than_t')}
      </div>
    </>
  );
}
